import { useCallback } from "react";
import {
  createNotificationSetter,
  currentAccountIdAtom,
} from "@zapier/canvas-state";
import { useAtomValue, useSetAtom } from "jotai";
import { Button } from "@zapier/design-system";
import { ToolComponentProps } from "@zapier/ai-toolkit/client";
import { useCreateAppConnection } from "../../shared/auth";
import styles from "../shared/buttonStyles.module.css";
import { CreateAuthParams, CreateAuthResult } from "./index";

export function CreateAuth(
  props: ToolComponentProps<typeof CreateAuthParams, CreateAuthResult>,
) {
  const { args } = props;
  const selected_api = args?.selected_api;
  const createNotification = useSetAtom(createNotificationSetter);
  const accountId = useAtomValue(currentAccountIdAtom);
  const { createNewConnection } = useCreateAppConnection(Number(accountId));

  const handleButtonClick = useCallback(async () => {
    if (!selected_api) {
      return;
    }

    // eslint-disable-next-line no-console
    console.log(`[CREATE_AUTH] Starting auth creation for: ${selected_api}`);
    const appNameWithoutVersion = selected_api.split("@")[0];

    const getDisplayName = (apiId: string) => {
      const nameWithoutVersion = apiId
        .split("@")[0]
        .replace(/CLIAPI$/, "")
        .replace(/API$/, "");

      // Handle specific Zapier apps
      if (nameWithoutVersion === "AI") return "AI by Zapier";
      if (nameWithoutVersion === "Filter") return "Filter by Zapier";
      if (nameWithoutVersion === "Formatter") return "Formatter by Zapier";
      if (nameWithoutVersion === "Delay") return "Delay by Zapier";
      if (nameWithoutVersion === "Digest") return "Digest by Zapier";
      if (nameWithoutVersion === "Email") return "Email by Zapier";
      if (nameWithoutVersion === "RSS") return "RSS by Zapier";
      if (nameWithoutVersion === "SubFormatter")
        return "Sub-Formatter by Zapier";
      if (nameWithoutVersion === "Webhooks") return "Webhooks by Zapier";
      if (nameWithoutVersion === "Storage") return "Storage by Zapier";
      if (nameWithoutVersion === "Schedule") return "Schedule by Zapier";
      if (nameWithoutVersion === "Paths") return "Paths by Zapier";
      if (nameWithoutVersion === "Branching") return "Paths by Zapier";
      if (nameWithoutVersion === "Tables") return "Tables by Zapier";
      if (nameWithoutVersion === "Interfaces") return "Interfaces by Zapier";
      if (nameWithoutVersion === "Chatbots") return "Chatbots by Zapier";

      // Handle Google apps
      if (nameWithoutVersion === "GoogleSheetsV2") return "Google Sheets";
      if (nameWithoutVersion === "GoogleCalendar") return "Google Calendar";
      if (nameWithoutVersion === "GoogleDrive") return "Google Drive";
      if (nameWithoutVersion === "GoogleMail") return "Gmail";

      return nameWithoutVersion;
    };

    // Check if this is a Zapier internal app that doesn't need authentication
    const zapierApps = [
      "AICLIAPI", // AI by Zapier
      "FilterCLIAPI", // Filter by Zapier
      "FilterAPI", // Filter by Zapier (alternate)
      "FormatterCLIAPI", // Formatter by Zapier
      "DelayAPI", // Delay by Zapier
      "DigestCLIAPI", // Digest by Zapier
      "EmailCLIAPI", // Email by Zapier
      "RSSCLIAPI", // RSS by Zapier
      "SubFormatterCLIAPI", // Sub-Formatter by Zapier
      "WebhooksCLIAPI", // Webhooks by Zapier
      "StorageCLIAPI", // Storage by Zapier
      "ScheduleCLIAPI", // Schedule by Zapier
      "PathsCLIAPI", // Paths by Zapier
      "BranchingAPI", // Paths by Zapier (alternate)
      "TablesAPI", // Tables by Zapier
      "TablesCLIAPI", // Tables by Zapier (alternate)
      "InterfacesCLIAPI", // Interfaces by Zapier
      "InterfacesAPI", // Interfaces by Zapier (alternate)
      "ChatbotsAPI", // Chatbots by Zapier
      "ChatbotsCLIAPI", // Chatbots by Zapier (alternate)
    ];

    if (
      zapierApps.includes(appNameWithoutVersion) ||
      appNameWithoutVersion.toLowerCase().includes("zapier") ||
      appNameWithoutVersion.toLowerCase().includes("byZapier")
    ) {
      const cleanAppName = getDisplayName(selected_api);

      window.postMessage(
        {
          type: "COPILOT_USER_MESSAGE",
          payload: {
            message: `✅ ${cleanAppName} is a Zapier internal app and doesn't require authentication. It's ready to use!`,
            source: "create-auth-zapier-app",
          },
        },
        "*",
      );
      return;
    }

    try {
      // Fetch the API metadata using search endpoint
      // NOTE: Always use /implementations-meta/search, NEVER /implementations
      // (which returns all implementations and should be avoided)
      // Convert app name to search-friendly term (e.g., MailchimpCLIAPI -> mailchimp)
      const searchTerm = appNameWithoutVersion
        .replace(/CLIAPI$/, "")
        .toLowerCase();
      const res = await fetch(
        `/api/v4/implementations-meta/search?term=${searchTerm}`,
      );
      const data = await res.json();

      // Check if we have valid API data and find the matching implementation
      if (!data.results || data.results.length === 0) {
        // Try alternative search terms for common apps
        let alternativeSearchTerm = searchTerm;
        if (searchTerm === "googlecalendar") {
          alternativeSearchTerm = "google calendar";
        } else if (searchTerm === "googlemailv2") {
          alternativeSearchTerm = "gmail";
        } else if (searchTerm === "googlemail") {
          alternativeSearchTerm = "gmail";
        } else if (searchTerm === "googledrive") {
          alternativeSearchTerm = "google drive";
        } else if (searchTerm === "googlesheetsv2") {
          alternativeSearchTerm = "google sheets";
        } else if (searchTerm.startsWith("google") && searchTerm.length > 6) {
          // For any google* app, try "google [service]"
          const service = searchTerm.replace(/^google/, "").replace(/v2$/, "");
          alternativeSearchTerm = `google ${service}`;
        }

        if (alternativeSearchTerm !== searchTerm) {
          // eslint-disable-next-line no-console
          console.log(
            `[CREATE_AUTH] Trying alternative search term: ${alternativeSearchTerm}`,
          );
          const altRes = await fetch(
            `/api/v4/implementations-meta/search?term=${alternativeSearchTerm}`,
          );
          const altData = await altRes.json();

          if (altData.results && altData.results.length > 0) {
            Object.assign(data, altData); // Use alternative results
          }
        }

        if (!data.results || data.results.length === 0) {
          throw new Error(
            `No API metadata found for ${searchTerm} (also tried: ${alternativeSearchTerm})`,
          );
        }
      }

      // Find the app that matches our original implementation ID
      const matchingApp =
        data.results.find(
          (app: any) =>
            app.id === selected_api || app.id.startsWith(appNameWithoutVersion),
        ) || data.results[0]; // Fallback to first result if no exact match

      // Get full app details with authentication info using the implementations endpoint
      // The search endpoint doesn't include authType and other auth fields
      const fullDetailsRes = await fetch(
        `/api/v4/implementations?selected_apis=${matchingApp.id}`,
      );
      const fullDetailsData = await fullDetailsRes.json();

      if (!fullDetailsData.results || !fullDetailsData.results[0]) {
        throw new Error(
          `No full implementation details found for ${matchingApp.id}`,
        );
      }

      let fullAppData = fullDetailsData.results[0];

      // Ensure fullAppData always has an id field for createNewConnection
      fullAppData.id = fullAppData.id || matchingApp.id || selected_api;

      // First check using the same logic as get_app_auth to be consistent
      const checkAuthUrl = `/api/canvasui/composer/getAppAuth?implementation_id=${encodeURIComponent(selected_api)}`;
      // eslint-disable-next-line no-console
      console.log(
        `[CREATE_AUTH] Checking auth requirements at: ${checkAuthUrl}`,
      );

      let authCheckSucceeded = false;
      try {
        const authCheckResponse = await fetch(checkAuthUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "include",
        });

        // eslint-disable-next-line no-console
        console.log(
          `[CREATE_AUTH] Auth check response status: ${authCheckResponse.status}`,
        );

        if (authCheckResponse.ok) {
          const authCheckData = await authCheckResponse.json();
          // eslint-disable-next-line no-console
          console.log(`[CREATE_AUTH] Auth check data:`, authCheckData);
          authCheckSucceeded = true;

          // If the backend says app doesn't need auth, respect that
          if (
            authCheckData.error &&
            authCheckData.error.includes("does not require")
          ) {
            const cleanAppName = appNameWithoutVersion.replace(/CLIAPI$/, "");
            // eslint-disable-next-line no-console
            console.log(
              `[CREATE_AUTH] ${selected_api} does not support authentication`,
            );
            window.postMessage(
              {
                type: "COPILOT_USER_MESSAGE",
                payload: {
                  message: `ℹ️ ${cleanAppName} does not require authentication. You can use it directly in your Zap workflows without connecting.`,
                  source: "create-auth-info",
                  messageType: "text",
                },
              },
              "*",
            );
            return;
          }

          // If auth check succeeded, it means the app DOES require auth but might not have any yet
          // eslint-disable-next-line no-console
          console.log(
            `[CREATE_AUTH] ${selected_api} requires authentication, proceeding with auth creation`,
          );
        } else {
          const errorData = await authCheckResponse.json();
          // eslint-disable-next-line no-console
          console.log(`[CREATE_AUTH] Auth check failed with error:`, errorData);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(
          `[CREATE_AUTH] Auth check failed, falling back to implementation check:`,
          error,
        );
      }

      // WORKAROUND: If implementations API returns incomplete authType, but our backend confirms auth is needed
      if (
        (!fullAppData.authType ||
          fullAppData.authType === "null" ||
          fullAppData.authType === "") &&
        authCheckSucceeded
      ) {
        // eslint-disable-next-line no-console
        console.log(
          `[CREATE_AUTH] Implementations API missing authType for ${selected_api}, but backend confirms auth needed. Defaulting to oauth.`,
        );

        // Default to oauth for apps that require auth but have missing authType metadata
        fullAppData = {
          ...fullAppData,
          authType: "oauth",
        };
      }

      // Continue with original fallback logic if auth check didn't succeed
      if (!authCheckSucceeded) {
        // Fallback to implementation metadata check
        const hasAuthType =
          fullAppData.authType && fullAppData.authType !== "null";
        const hasAuthMeta =
          fullAppData.authentication || fullAppData.auth_fields;

        if (!hasAuthType && !hasAuthMeta) {
          const cleanAppName = appNameWithoutVersion.replace(/CLIAPI$/, "");
          window.postMessage(
            {
              type: "COPILOT_USER_MESSAGE",
              payload: {
                message: `ℹ️ ${cleanAppName} does not require authentication. You can use it directly in your Zap workflows without connecting.`,
                source: "create-auth-info",
                messageType: "text",
              },
            },
            "*",
          );
          return;
        }
      }

      // Create the connection using the auth popup with complete metadata
      // eslint-disable-next-line no-console
      console.log(`[CREATE_AUTH] Calling createNewConnection with app data:`, {
        id: fullAppData.id,
        name: fullAppData.name,
        authType: fullAppData.authType,
        hasUpfrontFields: fullAppData.hasUpfrontFields,
        authentication: fullAppData.authentication,
        auth_fields: fullAppData.auth_fields,
      });

      const result = await createNewConnection(fullAppData);

      // Check if connection was successful
      if (result && result.success && result.id) {
        // Send success message to chat
        window.postMessage(
          {
            type: "COPILOT_USER_MESSAGE",
            payload: {
              message: `✅ Successfully connected to ${appNameWithoutVersion}! Connection ID: ${result.id}`,
              source: "create-auth-success",
              messageType: "text",
            },
          },
          "*",
        );

        // Also show a notification
        createNotification({
          message: `New connection to ${appNameWithoutVersion} added`,
          type: "success",
        });

        // IMPORTANT: Auto-trigger confirm_auth to apply authentication to steps
        // This ensures auth is automatically ready for field mapping without manual intervention
        setTimeout(() => {
          window.postMessage(
            {
              type: "COPILOT_USER_MESSAGE",
              payload: {
                message: `Please use confirm_auth with implementation_id: "${selected_api}", auth_id: "${result.id}", auth_label: "Connection to ${appNameWithoutVersion}"`,
                source: "auto-confirm-auth",
                messageType: "text",
              },
            },
            "*",
          );
        }, 500); // Small delay to ensure success message is processed first
      } else {
        // Add failure result to the chat
        console.error("Connection failed. Result:", result);
        const errorMessage =
          result?.error instanceof Error
            ? result.error.message
            : "Connection failed";

        window.postMessage(
          {
            type: "COPILOT_USER_MESSAGE",
            payload: {
              message: `❌ Failed to connect to ${appNameWithoutVersion}: ${errorMessage}`,
              source: "create-auth-error",
              messageType: "text",
            },
          },
          "*",
        );
      }
    } catch (error) {
      // Send error message to chat
      window.postMessage(
        {
          type: "COPILOT_USER_MESSAGE",
          payload: {
            message: `❌ Error connecting to ${appNameWithoutVersion}: ${error instanceof Error ? error.message : "Unknown error"}`,
            source: "create-auth-error",
            messageType: "text",
          },
        },
        "*",
      );
    }
  }, [selected_api, createNewConnection, createNotification]);

  if (!args || !selected_api) {
    return null;
  }

  const getDisplayName = (apiId: string) => {
    const nameWithoutVersion = apiId
      .split("@")[0]
      .replace(/CLIAPI$/, "")
      .replace(/API$/, "");

    // Handle specific Zapier apps
    if (nameWithoutVersion === "AI") return "AI by Zapier";
    if (nameWithoutVersion === "Filter") return "Filter by Zapier";
    if (nameWithoutVersion === "Formatter") return "Formatter by Zapier";
    if (nameWithoutVersion === "Delay") return "Delay by Zapier";
    if (nameWithoutVersion === "Digest") return "Digest by Zapier";
    if (nameWithoutVersion === "Email") return "Email by Zapier";
    if (nameWithoutVersion === "RSS") return "RSS by Zapier";
    if (nameWithoutVersion === "SubFormatter") return "Sub-Formatter by Zapier";
    if (nameWithoutVersion === "Webhooks") return "Webhooks by Zapier";
    if (nameWithoutVersion === "Storage") return "Storage by Zapier";
    if (nameWithoutVersion === "Schedule") return "Schedule by Zapier";
    if (nameWithoutVersion === "Paths") return "Paths by Zapier";
    if (nameWithoutVersion === "Branching") return "Paths by Zapier";

    // Handle Google apps
    if (nameWithoutVersion === "GoogleSheetsV2") return "Google Sheets";
    if (nameWithoutVersion === "GoogleCalendar") return "Google Calendar";
    if (nameWithoutVersion === "GoogleDrive") return "Google Drive";
    if (nameWithoutVersion === "GoogleMail") return "Gmail";

    return nameWithoutVersion;
  };

  const displayName = getDisplayName(selected_api);

  return (
    <div className={styles.container}>
      <Button variant="primary" size="medium" onPress={handleButtonClick}>
        Connect to {displayName}
      </Button>
    </div>
  );
}
